import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { CR_API_ENDPOINTS } from '../../constants';
import { apiService } from '../../services';
import { ClientResponse, LenderResponse } from '../../types/lender-packages.types';
import { ImportCard } from './components/ImportCard';
import { LenderImportCard } from './components/LenderImportCard';

type ImportTab = 'client' | 'lender';

/**
 * Hidden admin page for importing data from Excel files.
 * Accessible at /import (no navigation link).
 *
 * Provides two tabbed sections:
 * - Lender Packages: import BOM/package data for a selected lender
 * - Client Catalog (NetSuite Items): import item catalog for a selected client
 */
export const ImportPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ImportTab>('client');
  const [lenders, setLenders] = useState<ReadonlyArray<LenderResponse>>([]);
  const [clients, setClients] = useState<ReadonlyArray<ClientResponse>>([]);
  const [isLoadingLenders, setIsLoadingLenders] = useState(true);
  const [isLoadingClients, setIsLoadingClients] = useState(true);

  useEffect(() => {
    const fetchLenders = async (): Promise<void> => {
      try {
        const data = await apiService.get<LenderResponse[]>(CR_API_ENDPOINTS.LENDERS);
        setLenders(data);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load lenders';
        toast.error(message);
      } finally {
        setIsLoadingLenders(false);
      }
    };

    const fetchClients = async (): Promise<void> => {
      try {
        const data = await apiService.get<ClientResponse[]>(CR_API_ENDPOINTS.CLIENTS);
        setClients(data);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load clients';
        toast.error(message);
      } finally {
        setIsLoadingClients(false);
      }
    };

    fetchLenders();
    fetchClients();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-10">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl font-semibold text-slate-900 mb-1">Data Import</h1>
        <p className="text-sm text-slate-500 mb-6">
          Import lender packages or client catalog data from Excel spreadsheets.
        </p>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200 mb-0">
          <button
            onClick={() => setActiveTab('client')}
            className={`px-5 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'client'
                ? 'bg-white text-blue-700 border border-slate-200 border-b-white -mb-px'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
            }`}
          >
            Client Catalog
          </button>
          <button
            onClick={() => setActiveTab('lender')}
            className={`px-5 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'lender'
                ? 'bg-white text-amber-700 border border-slate-200 border-b-white -mb-px'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
            }`}
          >
            Lender Packages
          </button>
        </div>

        {/* Tab content */}
        <div className="bg-white border border-t-0 border-slate-200 rounded-b-2xl rounded-tr-2xl p-6">
          {activeTab === 'lender' && (
            <LenderImportCard
              lenders={lenders}
              isLoadingLenders={isLoadingLenders}
            />
          )}

          {activeTab === 'client' && (
            <ImportCard
              title="Client Catalog Import"
              description="Upload an Excel file to import NetSuite item catalog data."
              entities={clients}
              entityLabel="Client"
              uploadEndpoint={`${CR_API_ENDPOINTS.NETSUITE_ITEMS}/import`}
              isLoadingEntities={isLoadingClients}
            />
          )}
        </div>
      </div>
    </div>
  );
};
